// LxTabs — Lamtix-token-wrapped Tabs primitive
//
// RTL audit:
//   - TabsList uses justify-start (Tailwind) → items flow from inline-start in RTL
//   - Trigger text flows with page direction (Arabic RTL, Latin LTR)
//   - No directional hardcoding in tab logic
//   - bg-muted / bg-background / text-foreground — semantic only
//
// D-SHADCN-LAMTIX-WRAPPED-01: sole import path is @lamtix/ui
import * as React from 'react';
import { cn } from '../utils';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../shadcn/tabs';

// ── Re-exports with Lx prefix ─────────────────────────────────────────────────

const LxTabs = Tabs;

const LxTabsList = React.forwardRef<
  React.ElementRef<typeof TabsList>,
  React.ComponentPropsWithoutRef<typeof TabsList>
>(({ className, ...props }, ref) => (
  <TabsList
    ref={ref}
    className={cn(
      // justify-start: tabs flow from inline-start in both LTR and RTL
      'inline-flex h-10 items-center justify-start rounded-md bg-muted p-1 text-muted-foreground w-full overflow-x-auto',
      className,
    )}
    {...props}
  />
));
LxTabsList.displayName = 'LxTabsList';

const LxTabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsTrigger>,
  React.ComponentPropsWithoutRef<typeof TabsTrigger>
>(({ className, ...props }, ref) => (
  <TabsTrigger
    ref={ref}
    className={cn(
      'inline-flex items-center justify-center whitespace-nowrap rounded-sm px-4 py-1.5 text-sm font-medium',
      'ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
      'disabled:pointer-events-none disabled:opacity-50',
      'data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm',
      className,
    )}
    {...props}
  />
));
LxTabsTrigger.displayName = 'LxTabsTrigger';

const LxTabsContent = React.forwardRef<
  React.ElementRef<typeof TabsContent>,
  React.ComponentPropsWithoutRef<typeof TabsContent>
>(({ className, ...props }, ref) => (
  <TabsContent
    ref={ref}
    className={cn(
      'mt-4 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
      className,
    )}
    {...props}
  />
));
LxTabsContent.displayName = 'LxTabsContent';

export { LxTabs, LxTabsList, LxTabsTrigger, LxTabsContent };
